import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { AppDispatch, RootState } from "services/redux/rootReducer";
import loginUser from "../actions/auth.loginUser";
import { LoginCredentials } from "features/auth/types/credentials";
import LoadingScreen from "features/shared/screens/loading";
import { IoIosLogIn } from "react-icons/io";
import Logo from "assets/customIcons/logo";

const LoginPage: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const { isLoggedIn, hasLoaded } = useSelector(
        (state: RootState) => state.auth
    );
    const history = useHistory();
    const [fields, setFields] = useState<LoginCredentials>({
        email: "",
        password: "",
    });
    const [isSubmitting, setSubmitting] = useState(false);

    const handleSubmitForm = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting) return;
        setSubmitting(true);
        dispatch(loginUser(fields)).catch(() => {
            alert("Invalid login");
            setSubmitting(false);
        });
    };

    // Have to use the redux state to check login as using .then()
    // from the firebase promise causes the redirect to be too fast
    useEffect(() => {
        if (isLoggedIn) history.push("/dashboard");
    }, [isLoggedIn, history]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFields({
            ...fields,
            [event.target.name]: event.target.value,
        });
    };

    // Special case for if user has logged in but hasn't had auth state loaded yet
    if (!hasLoaded || isLoggedIn) return <LoadingScreen />;

    return (
        <Container>
            <Subcontainer>
                <StyledText>
                    <Logo />
                </StyledText>
                <StyledForm onSubmit={handleSubmitForm} >
                    <StyledInput
                        name="email"
                        value={fields.email}
                        type="email"
                        placeholder="Email"
                        onChange={handleInputChange}
                    />
                    <StyledInput
                        name="password"
                        value={fields.password}
                        type="password"
                        placeholder="Password"
                        onChange={handleInputChange}
                    />
                    <StyledButton type="submit" isSubmitting={isSubmitting}>
                        {isSubmitting ? (
                            <span>Loading...</span>
                        ) : (
                            <>
                                <span>Sign In</span>
                                <IoIosLogIn />
                            </>
                        )}
                    </StyledButton>
                </StyledForm>
            </Subcontainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background: #e0eafc; /* fallback for old browsers */
    background: -webkit-linear-gradient(
        to right,
        #cfdef3,
        #e0eafc
    ); /* Chrome 10-25, Safari 5.1-6 */
    background: linear-gradient(
        to right,
        #cfdef3,
        #e0eafc
    ); /* W3C, IE 10+/ Edge, Firefox 16+, Chrome 26+, Opera 12+, Safari 7+ */

    height: 100vh;
`;

const Subcontainer = styled.div`
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    background: white;
    border-radius: 0.2rem;

    box-shadow: 1px 0px 10px 1px rgba(214, 214, 214, 0.25);
`;

const StyledText = styled.div`
    display: flex;
    flex-flow: column;

    font-size: 2rem;
    font-weight: ${(props) => props.theme.fontWeight.bolder};

    width: 12rem;
`;

const StyledForm = styled.form`
    display: flex;
    flex-flow: column;
    justify-content: space-around;
    align-items: center;
    margin-top: 24px;
`;

const StyledInput = styled.input`
    width: 18rem;
    height: 2.6rem;
    margin-bottom: 0.6rem;
    border: solid 1px ${(props) => props.theme.colors.neutral[200]};
    border-radius: 0.2rem;
    color: ${(props) => props.theme.colors.neutral[600]};
    box-shadow: 0px 8px 15px rgba(205, 212, 220, 0.15);
    transition: all 0.08s linear;
    padding-left: 0.8rem;

    &:hover,
    &:focus {
        border: solid 1px ${(props) => props.theme.colors.neutral[400]};
    }

    &::placeholder {
        color: ${(props) => props.theme.colors.neutral[400]};
    }
`;

const StyledButton = styled.button<{ isSubmitting: boolean }>`
    width: 8rem;
    height: 2.6rem;
    background: ${(props) =>
        props.isSubmitting
            ? props.theme.colors.neutral[400]
            : props.theme.colors.primary[500]};
    border-radius: 0.2rem;
    box-shadow: 0px 8px 15px rgba(57, 120, 198, 0.15);
    color: ${(props) => props.theme.colors.neutral[100]};
    font-weight: ${(props) => props.theme.fontWeight.bold};
    margin-top: 1rem;
    transition: all 0.08s linear;
    display: flex;
    justify-content: space-evenly;
    align-items: center;

    ${(props) =>
        props.isSubmitting
            ? ``
            : `
            &:hover {
        cursor: pointer;
        background: ${props.theme.colors.primary[400]};
        transform: scale(1.02);
    }
    `}

    & > * {
        &:nth-child(2) {
            stroke-width: 1.1rem;
            transform: scale(1.25);
        }
    }
`;

export default LoginPage;
